import { PrismicRichText, PrismicText } from '@prismicio/react';
import { hasRichText } from '@publicss/utils';
import classNames from 'classnames';
import React from 'react';
import styled, { css } from 'styled-components';

import { useIsRTL } from '../hooks/useIsRTL';
import { usePageContext } from '../providers/PageContext';
import { media } from '../styles/media';
import { useIsMobile } from '../utils/breakpoints';
import H1orH2 from './H1orH2';
import Play from './icons/Play';
import { Button } from './ui';
import ModalVideo from './ui/ModalVideo';
import ScrollIndicator from './ui/ScrollIndicator';

const BlockHeroPhotoRightSlice = (props) => {
  const { siteTag } = usePageContext();
  const isMobile = useIsMobile();
  const [active, setActive] = React.useState(false);

  const { slice, sliceIndex } = props;
  const { primary } = slice;
  const {
    heading_start,
    heading_end,
    description,
    description_size,
    button,
    button_link,
    background,
    video,
    video_button_text,
  } = primary;

  const hasButton = hasRichText(button);
  const hasVideo = video?.url && video_button_text;
  const isRTL = useIsRTL();

  return (
    <Section
      siteTag={siteTag}
      isRTL={isRTL}
      url={background.url}
      id="block-hero-photo">
      <div className="container">
        <div className="mt-40 grid sm:grid-cols-4 md:mt-0 md:grid-cols-8 lg:grid-cols-12">
          <div
            className="lg:pe-22 bg--primary-dark bg--left fade-in-1
            relative col-span-6 pb-16 pe-10 pt-16 md:col-span-5 md:mb-20 md:mt-20 md:pe-16 md:pt-16 lg:col-span-6 lg:mb-24
            lg:mt-24 lg:pb-24 lg:pt-24 xl:col-span-5">
            <div className="relative z-10">
              <H1orH2
                sliceIndex={sliceIndex}
                className={classNames(
                  'color--white fade-up mb-10 md:mb-12',
                  siteTag === 'site:kitchenvalley' ? 'h2-kv' : 'h2'
                )}>
                <PrismicRichText field={heading_start.richText} />
                <span className="color--accent-1">
                  {' '}
                  <PrismicRichText field={heading_end.richText} />
                </span>
              </H1orH2>
              <div
                className={classNames(
                  'fade-up color--white mb-10 md:mb-12',
                  description_size === 'large' ? 'text-2xl' : ''
                )}>
                <PrismicRichText field={description.richText} />
              </div>
              <div className="flex flex-wrap gap-x-8 gap-y-6 sm-down:justify-center">
                {hasButton && (
                  <>
                    {button_link?.url ? (
                      <Button
                        variant="inverse"
                        href={button_link?.url}
                        fullWidth={isMobile && !hasVideo}
                        className="fade-up">
                        <PrismicText field={button.richText} />
                      </Button>
                    ) : (
                      <Button
                        globalTypeform
                        variant="inverse"
                        className="fade-up sm-down:w-full">
                        <PrismicText field={button.richText} />
                      </Button>
                    )}
                  </>
                )}
                {hasVideo && (
                  <div className="fade-up flex items-center">
                    <button
                      onClick={() => setActive(true)}
                      className="btn--text--play whitespace-nowrap bg-[transparent] text-white">
                      {video_button_text}
                      <span className="rtl:-translate-x-2 rtl:rotate-180 rtl:[&_svg]:-translate-x-[6px]">
                        <Play />
                      </span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
          <ScrollIndicator />
        </div>
      </div>
      <div
        className="bg--hero--holder fade-in-2"
        style={{
          background: `var(--grey-2) url(${background.url}) no-repeat`,
        }}>
        <div className="bg--hero" />
      </div>
      {hasVideo && (
        <ModalVideo
          videoSrc={video?.url}
          open={active}
          onClose={() => setActive(false)}
        />
      )}
    </Section>
  );
};

export default BlockHeroPhotoRightSlice;

const Section = styled.section<{
  url: string;
  isRTL: boolean;
  siteTag: string;
}>`
  p a {
    text-decoration: underline;
  }
  .h2 p {
    line-height: 92%;
  }
  position: relative;
  overflow: hidden;
  min-height: 616px;
  @media ${media.mdUp} {
    min-height: 696px;
  }
  @media ${media.lgUp} {
    min-height: 843px;
  }
  .bg--hero--holder {
    position: absolute;
    z-index: -1;
    top: 0;
    inset-inline-end: 0;
    ${({ isRTL }) =>
      isRTL
        ? css`
            transform: scaleX(-1);
          `
        : css``}
    width: 100%;
    height: 300px;
    overflow: hidden;
    @media ${media.mdUp} {
      width: 75%;
      height: 100%;
    }
  }
  .bg--hero {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    ${(props) => {
      return css`
        background: var(--grey-2) url(${props.url}) no-repeat;
      `;
    }}
    background-position: bottom;
    background-size: cover;
    overflow: hidden;
    @media ${media.mdUp} {
      background-size: cover;
      background-position: bottom center;
    }
  }
  .bg--left {
    &::before {
      position: absolute;
      z-index: 1;
      content: '';
      bottom: 0;
      inset-inline-start: -100%;
      height: 100%;
      width: 200%;
      background: var(--primary);
    }
  }

  ${({ siteTag }) =>
    siteTag === 'site:profood-solutions' &&
    css`
      .bg--hero--holder {
        @media ${media.mdUp} {
          width: 100%;
        }
      }
    `}
`;
